import { useSelector } from '@xstate/react';
import cn from 'classnames';
import { useCallback, useMemo, useState } from 'react';

import { CheckboxDumb } from '@webapp/common/components/checkbox-dumb';
import { useTextStyle } from '@webapp/common/components/custom-styles';
import { GradientNumbers } from '@webapp/common/components/gradient-numbers';
import { NumbersWithBorders } from '@webapp/common/components/numbers-with-borders';
import { Slider } from '@webapp/common/components/slider';
import { CssPageBlock, CssQuestionType, CssUiComponent } from '@webapp/common/components/survey-custom';
import { useMobile } from '@webapp/common/hooks/use-mobile';
import { setHtml } from '@webapp/common/lib/ui';
import { CANTRATE_ID } from '@webapp/common/resources/constants';
import { PLACEHOLDER } from '@webapp/common/resources/fillers';

import type { SurveyQuestion } from '@webapp/survey/src/components/questions/lib';
import type { QuestionGroupEntity } from '@webapp/survey/src/entities/question-group';
import type { QuestionResponseContext } from '@webapp/survey/src/entities/response-machine';

import { LeftRight } from '../left-right';

import css from './scale.css';

const Groups: FC<{
    onClick: (value: number) => () => void;
    groups: Array<QuestionGroupEntity>;
    regularStyle: CSSProperties;
    selectedStyle: CSSProperties;
    response: QuestionResponseContext;
}> = ({ groups, onClick, regularStyle, response, selectedStyle }) => (
    <div className={cn(CssPageBlock.ANSWER, css.field)}>
        {groups.map((group) => {
            const { id, name } = group;
            const isSelected = response.value === group.id;

            return (
                <div
                    className={cn(CssUiComponent.LABEL, css.item, { [css.selected]: isSelected })}
                    key={id}
                    style={isSelected ? selectedStyle : regularStyle}
                    onClick={onClick(id)}
                    {...setHtml(name)}
                />
            );
        })}
    </div>
);

export const Scale: FC<SurveyQuestion> = ({ question: { answersActors, responseActor, groups, ...rest }, actor }) => {
    const {
        value: [leftLabel, , rightLabel],
    } = useSelector(answersActors[0], (a) => a.context);
    const response = useSelector(responseActor, (r) => r.context);

    const {
        id: questionId,
        params: {
            cantRate,
            cantRateText,
            scaleColors,
            scaleEndDia,
            scaleStartDia,
            scaleType,
            scaleFormat,
            npsLeftLabel,
            npsRightLabel,
            size,
        },
    } = rest;

    const isMobile = useMobile();
    const { answerStyle, textStyle } = useTextStyle();
    const fontSize = isMobile ? `clamp(16px, calc(10px + 1.5vw), 16px)` : textStyle.fontSize;

    const selectedStyle: CSSProperties = useMemo(
        () => ({
            ...textStyle,
            boxShadow: `inset 0px 0px 0px 2px ${answerStyle.color}`,
            fontSize,
        }),
        [textStyle, answerStyle.color, fontSize],
    );

    const regularStyle = useMemo(
        () => ({
            ...textStyle,
            fontSize,
        }),
        [fontSize, textStyle],
    );

    const isCheckedInit = response?.value === CANTRATE_ID;

    const [cantRateChecked, setCantRateChecked] = useState(isCheckedInit);

    const handleChecked = useCallback(
        (checked) => {
            setCantRateChecked(checked);
            responseActor.send({ type: 'CHANGE', value: checked ? CANTRATE_ID : '', questionId });
            actor.send({ type: 'QUESTION_RESPONSE_CHANGE', questionId, value: checked ? CANTRATE_ID : '' });
        },
        [response],
    );

    const handleSelect = useCallback(
        (value: number) => () => {
            setCantRateChecked(false);

            responseActor.send({ type: 'CHANGE', value, questionId });
            actor.send({ type: 'QUESTION_RESPONSE_CHANGE', questionId, value });
        },
        [response],
    );

    const selected = useMemo(() => groups.find((group) => group.id === response.value), [response.value, groups]);

    const renderContent = () => {
        if (scaleType !== 'number') {
            return (
                <LeftRight
                    needToCheckHorizontal={true}
                    className={CssQuestionType.SCALE}
                    left={leftLabel}
                    right={rightLabel}
                >
                    <Groups
                        groups={groups}
                        regularStyle={regularStyle}
                        response={response}
                        selectedStyle={selectedStyle}
                        onClick={handleSelect}
                    />
                </LeftRight>
            );
        }
        if (scaleFormat === 'numbers') {
            return (
                <LeftRight
                    needToCheckHorizontal={true}
                    className={cn(CssQuestionType.SCALE, css.leftRight)}
                    left={npsLeftLabel}
                    right={npsRightLabel}
                >
                    <GradientNumbers
                        minWidthForItems={true}
                        selectedValue={selected?.numericValue}
                        colors={scaleColors}
                        itemsCount={scaleEndDia - scaleStartDia + 1}
                        startsWith={scaleStartDia}
                        size={size === 1 ? 'small' : 'normal'}
                        onClick={(value) => handleSelect(groups.find((group) => group.numericValue === value)?.id)()}
                    />
                </LeftRight>
            );
        }
        if (scaleFormat === 'numbers-with-borders') {
            return (
                <LeftRight
                    needToCheckHorizontal={true}
                    className={cn(CssQuestionType.SCALE, css.leftRight, css.leftRightNumbersWithBorder)}
                    left={npsLeftLabel}
                    right={npsRightLabel}
                >
                    <NumbersWithBorders
                        selectedValue={selected?.numericValue}
                        colors={scaleColors}
                        itemsCount={scaleEndDia - scaleStartDia + 1}
                        startsWith={scaleStartDia}
                        onClick={(value) => handleSelect(groups.find((group) => group.numericValue === value)?.id)()}
                    />
                </LeftRight>
            );
        }
        if (scaleFormat === 'slider') {
            return (
                <LeftRight
                    needToCheckHorizontal={true}
                    className={cn(CssQuestionType.SCALE, css.leftRight, css.leftRightSlider)}
                    left={npsLeftLabel}
                    right={npsRightLabel}
                >
                    <Slider
                        defaultValue={selected?.numericValue ?? null}
                        min={scaleStartDia}
                        max={scaleEndDia}
                        value={selected?.numericValue ?? null}
                        withoutControl
                        labelsBottom
                        handlerColor={scaleColors[0]}
                        trackBgImage={`linear-gradient(90deg, ${scaleColors.join(', ')})`}
                        onChange={(value) => handleSelect(groups.find((group) => group.numericValue === value)?.id)()}
                    />
                </LeftRight>
            );
        }
    };

    return (
        <div
            className={cn(
                css.container,
                'question__container',
                scaleType === 'number' && (scaleEndDia > 3 || scaleFormat === 'slider') && css.scaleTypeNumber,
                scaleType === 'number' &&
                    scaleFormat !== 'slider' &&
                    scaleEndDia < 4 &&
                    css.scaleTypeNumberLessThanFour,
            )}
        >
            {renderContent()}
            {cantRate && (
                <CheckboxDumb
                    checked={cantRateChecked}
                    className={css.cantRateCheckbox}
                    label={cantRateText || PLACEHOLDER.CANT_RATE}
                    round={false}
                    onChange={handleChecked}
                />
            )}
        </div>
    );
};
